import { useToast } from '@chakra-ui/react';
import { AccessLinkReadMetadata } from '@llx/common';
import { useCallback, useState } from 'react';

import {
  AccessLinkTypes,
  useBatchCreateAccessLinksMutation,
  useBatchUpdateAccessLinksMutation,
  useDeleteAccessLinkMutation,
  useListAccessLinksQuery,
} from '@/@generated/codegen/API';
import { usePaginationState } from '@/hooks/usePaginationState';
import { PageLoading } from '@/ui/PageLoading';
import { usePrevious } from '@/utils/helpers/react.helper';

import { AccessLinkPanelView } from './AccessLinkPanelView';
import {
  CreateAccessLinkFormValues,
  CreateAccessLinkPanelView,
} from './components/CreateAccessLinkPanelView';
import { useAccessLinksListQueryParams } from './hooks/useAccessLinksListQueryParams';

type Props = {
  type: AccessLinkTypes;
  objId: string;
  onClose: () => void;
};

const PAGE_SIZE = 100;
const tablePrefix = 'accessLinks';

export const AccessLinkPanelClient = ({ type, objId, onClose }: Props) => {
  const toast = useToast();
  const { orderByArgs, paginationArgs, where } = useAccessLinksListQueryParams(
    PAGE_SIZE,
    {
      purpose: {
        equals: type,
      },
      objId: {
        equals: objId,
      },
    },
    tablePrefix
  );
  const accessLinks = useListAccessLinksQuery({
    variables: {
      paginationArgs,
      orderByArgs,
      where,
    },
  });
  const previousData = usePrevious(accessLinks.data);
  const pagination = usePaginationState(PAGE_SIZE, tablePrefix);
  const [mutationBatchCreateAccessLinks, { loading: isCreating }] =
    useBatchCreateAccessLinksMutation();
  const [mutationBatchUpdateAccessLinks] = useBatchUpdateAccessLinksMutation();
  const [mutationDeleteAccessLink] = useDeleteAccessLinkMutation();

  const [showCreatePanel, setShowCreatePanel] = useState(false);

  const onCreateClick = useCallback(() => {
    setShowCreatePanel(true);
  }, []);

  const onRefreshClick = useCallback(() => {
    void accessLinks.refetch();
  }, []);

  const handleBatchEnable = useCallback(
    (obj: { id: string; isEnabled: boolean }[]) => {
      mutationBatchUpdateAccessLinks({
        variables: {
          data: {
            dtos: obj.map((el) => ({
              id: el.id,
              isEnabled: {
                set: el.isEnabled,
              },
            })),
          },
        },
      }).catch((err) => {
        toast({
          status: 'error',
          title: 'Error',
          description: err.message,
        });
      });
    },
    [mutationBatchUpdateAccessLinks]
  );

  const handleBatchDelete = useCallback(
    async (ids: string[]) => {
      // TODO: this should be a batch delete instead of lots of single deletes
      await Promise.all(
        ids.map(async (id) => {
          await mutationDeleteAccessLink({
            variables: {
              where: {
                id,
              },
            },
          });
        })
      );
    },
    [mutationDeleteAccessLink]
  );

  const handleCreateAccessLinks = useCallback(
    (values: CreateAccessLinkFormValues) => {
      mutationBatchCreateAccessLinks({
        variables: {
          data: {
            dtos: Array.from({ length: values.numberOfLinks }).map((_, index) => ({
              type: type,
              objId: objId,
              // usageCountLeft: values.usesPerLink,
              usageCountLeft: 1000000,
              validUntil: new Date(Date.now() + values.validityDays * 24 * 60 * 60 * 1000),
              linkFirstAccount: values.linkFirstAccount,
              forcedUsername: values.shouldForceUsernames ? values.forcedUsernames[index] : null,
              metadata: {
                isDemo: values.activateMetadataDemo ? true : undefined,
              } satisfies AccessLinkReadMetadata,
            })),
          },
        },
      })
        .then(async () => {
          toast({
            status: 'success',
            title: 'Link created',
          });
          await accessLinks.refetch();
          setShowCreatePanel(false);
        })
        .catch((err) => {
          toast({
            status: 'error',
            title: 'Error',
            description: err.message,
          });
        });
    },
    [mutationBatchCreateAccessLinks]
  );

  if (showCreatePanel) {
    return (
      <CreateAccessLinkPanelView
        type={type}
        onClose={() => {
          setShowCreatePanel(false);
        }}
        onSubmit={handleCreateAccessLinks}
        isCreating={isCreating}
      />
    );
  }

  if (!previousData && !accessLinks.data) {
    return <PageLoading />;
  }
  return (
    <AccessLinkPanelView
      type={type}
      data={accessLinks.data || previousData!}
      isLoading={accessLinks.loading}
      pagination={pagination}
      onBatchEnable={handleBatchEnable}
      onBatchDelete={handleBatchDelete}
      onClose={onClose}
      onCreateClick={onCreateClick}
      onRefreshClick={onRefreshClick}
      tablePrefix={tablePrefix}
    />
  );
};
